import React, { useContext } from 'react';
import styled from 'styled-components';
import { DropdownToggle } from 'reactstrap';
import _find from 'lodash/find';
import Icon from '@minecraft.icon';
import { ImageButton } from '@minecraft.atoms';
import SelectableDropdownContext from '../SelectableSearchDropdownContext';

/** Styled Components */
const DropdownHeader = styled(({ optionsLength, ...restProps }) => <DropdownToggle tag="div" {...restProps} />)`
  background-color: ${(props): string => props.theme.form.backgroundColor};
  padding: 0 0 0 0.1875rem;
  height: 2rem;
  width: 100%;

  border: 1px solid ${(props): string => props.theme.form.borderColor};
  border-radius: 0.25rem;

  display: flex;
  justify-content: ${(props): string => (props.optionsLength === 0 ? 'flex-end' : 'space-between')};
  align-items: center;

  &:hover,
  &:active,
  &:not(:disabled):not(.disabled):active,
  &:focus {
    box-shadow: none;
    background-color: ${(props): string => props.theme.form.backgroundColor};
    border-color: ${(props): string => props.theme.form.borderColor};
  }
`;

const ButtonsContainer = styled.span`
  color: ${(props): string => props.theme.typography.body.color};
  flex: 0 0 3rem;
  text-align: right;
`;

const CaretIcon = styled(Icon)`
  height: 1.25rem;

  margin: 0.375rem 0 0.375rem;
  padding: 0.25rem 0;
`;

const ClearIcon = styled(Icon)`
  height: 0.75rem;
`;

const StyledInput = styled.input<{ $isPlaceholderFontNormal?: boolean }>`
  background-color: transparent;
  border: none;
  outline: none;
  flex-grow: 1;
  margin-right: 1rem;
  min-width: 5rem; // fix for minimal input width

  &::placeholder {
    padding-left: 0.625rem;
    font-style: ${({ $isPlaceholderFontNormal }): string => ($isPlaceholderFontNormal ? 'normal' : 'italic')};
    font-size: 0.875rem;
    color: ${(props): string => props.theme.form.placeholderColor};
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
`;

/** Main Component */
const Header = ({
  allOptions,
  isPlaceholderFontNormal = false,
  toggleInputLabel = '',
}: {
  allOptions: any[];
  isPlaceholderFontNormal?: boolean;
  toggleInputLabel?: string;
}): JSX.Element => {
  const { saveAsOption, options, setOptions, filter, setFilter } = useContext(SelectableDropdownContext);

  let placeholderText = '';
  // if only one option is selected, display it's label
  if (options.length === 1 && allOptions.length >= 1) {
    placeholderText = _find(allOptions, { value: options[0]?.value || options[0] })?.label || '';
  } else if (saveAsOption && options.length === 1) {
    placeholderText = options[0]?.label;
  } else if (options.length > 0) {
    // if any options are selected, display number of options selected
    placeholderText = `${options.length} Selected`;
  }

  return (
    // should use className for style it when menu is opened
    <DropdownHeader className="dropdown-header" optionsLength={options.length}>
      <StyledInput
        onBlur={(): void => {
          // We need setTimeout hack here because a blur event fires before a click event that cause re-render of dropdown menu
          // and click target changes at the same moment
          setTimeout(() => {
            setFilter('');
          }, 300);
        }}
        placeholder={placeholderText}
        value={filter}
        onChange={(e): void => setFilter(e.target.value)}
        data-qa-id="selected-text"
        $isPlaceholderFontNormal={isPlaceholderFontNormal}
        aria-label={toggleInputLabel}
      />
      <ButtonsContainer>
        {options.length !== 0 && (
          <ImageButton
            data-qa-id="selectable-dropdown-clear-button"
            aria-label="Clear"
            type="button"
            onClick={(e): void => {
              e.stopPropagation();
              setOptions([]);
            }}
          >
            <ClearIcon inheritColor name="close" />
          </ImageButton>
        )}
        <span data-qa-id="selectable-dropdown-button">
          <CaretIcon inheritColor name="caret-1" />
        </span>
      </ButtonsContainer>
    </DropdownHeader>
  );
};

export default Header;
